/***
 * Copyright (c) Myia 2023-2025 - All Rights Reserved
 */

import { zodResolver } from '@hookform/resolvers/zod';
import { useState, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { mutate } from 'swr';
import * as z from 'zod';

import { useToast } from '../ui/use-toast';

import { OrganisationData } from '@/commons/commons';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '@/components/ui/dialog';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useMutate, useGet } from '@/net/commons';

const formSchema = z.object({
    email: z
        .string()
        .min(3, { message: 'Email is too short' })
        .max(100, { message: 'Email is too long' })
        .email({ message: 'Email is invalid' }),
    role: z.enum(['admin', 'member', 'operator'] as const),
});

export default function AddMemberDialog({ onInvite }: { onInvite: () => void }) {
    const [open, setOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const { toast } = useToast();
    const { data: organisation } = useGet<OrganisationData>('/bff/organisation');
    const isOperatorEnabled = useMemo(() => {
        if (!organisation) return false;
        const env = process.env.NODE_ENV;
        if (env === 'development') {
            return organisation.name === 'test-organisation';
        } else if (env === 'staging' || env === 'production') {
            return organisation.name === 'heal';
        }
        return false;
    }, [organisation]);

    const form = useForm<z.infer<typeof formSchema>>({
        defaultValues: {
            email: '',
            role: 'member',
        },
        resolver: zodResolver(formSchema),
    });
    const { trigger } = useMutate(`/bff/organisation/invitations`, {
        method: 'POST',
    });

    const onSubmit = async (values: z.infer<typeof formSchema>) => {
        try {
            setIsLoading(true);
            await trigger(values);
            mutate((key) => typeof key === 'string' && key.startsWith('/bff/organisation/invitations'), undefined, {
                revalidate: true,
            });
            setOpen(false);
            onInvite();
        } catch (err) {
            setIsLoading(false);
            toast({
                title: 'Failed to invite member',
                description: 'Please try again',
                variant: 'destructive',
            });
        }
    };

    return (
        <Dialog
            open={open}
            onOpenChange={(o) => {
                form.reset();
                setOpen(o);
                setIsLoading(false);
            }}
        >
            <DialogTrigger asChild>
                <Button className="w-max">Add Member</Button>
            </DialogTrigger>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Invite New Member</DialogTitle>
                    <DialogDescription>
                        If an invitation is sent, the user will receive an email with a link to join your organisation.
                        This invite will expire in 30 days.
                    </DialogDescription>
                </DialogHeader>
                <Form {...form}>
                    <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-10">
                        <FormField
                            control={form.control}
                            name="email"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel className="flex flex-row gap-2 items-center">Email</FormLabel>
                                    <FormControl>
                                        <Input placeholder="john@example.com" {...field} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="role"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel className="flex flex-row gap-2 items-center">Role</FormLabel>
                                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                                        <FormControl>
                                            <SelectTrigger>
                                                <SelectValue placeholder="Select a role" />
                                            </SelectTrigger>
                                        </FormControl>
                                        <SelectContent>
                                            <SelectItem value="admin">Admin</SelectItem>
                                            <SelectItem value="member">Member</SelectItem>
                                            {isOperatorEnabled && <SelectItem value="operator">Operator</SelectItem>}
                                        </SelectContent>
                                    </Select>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <div className="flex flex-row justify-end">
                            <Button type="submit" disabled={isLoading}>
                                {isLoading ? 'Inviting...' : 'Invite'}
                            </Button>
                        </div>
                    </form>
                </Form>
            </DialogContent>
        </Dialog>
    );
}
